import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { get, orderBy } from "lodash";

import firebase from "../../services/firebase";
import { createEmailTemplate, removeEmailTemplate, setEmailTemplate } from "../../services/emailTemplates";
import TextInputField from "../Shared/TextInputField";
import Textarea from "components/Shared/Textarea";
import PlaceholderHelpLink from "./PlaceholderHelpLink";

const EmailTemplate = () => {
  const [manualTemplates, setManualTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState("");
  const [tplName, setTplName] = useState("");
  const [tplSubject, setTplSubject] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [tplType, setTplType] = useState("MANUAL_EMAIL");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Goes up each time staff pick a template or click New, so a create or delete response
  // that comes back after they have moved on does not change the new selection.
  const selectionVersion = useRef(0);

  useEffect(() => {
    const emailTemplatesRef = firebase.database().ref("/email_templates");
    emailTemplatesRef.on("value", (snapshot) => {
      let manualTpls = [];
      let customTpls = [];
      const data = snapshot.val();
      for (let key in data) {
        if (data[key].type === "MANUAL_EMAIL") {
          manualTpls.push({ ...data[key], id: key });
        } else {
          customTpls.push({ ...data[key], id: key });
        }
      }
      setManualTemplates(manualTpls);
      setCustomTemplates(customTpls);
    });
    return () => {
      emailTemplatesRef.off();
    };
  }, []);

  const handleClickTemplate = (tpl) => {
    selectionVersion.current += 1;
    setErrorMessage("");
    setSelectedTplId(tpl.id);
    setTplName(tpl.name);
    setTplSubject(tpl.subject);
    setTplBody(tpl.body.replace(/<br \/>/g, "\n"));
    setTplType(tpl.type);
  };

  const handleClickNew = () => {
    selectionVersion.current += 1;
    setErrorMessage("");
    setSelectedTplId("");
    setTplName("");
    setTplSubject("");
    setTplBody("");
    setTplType("MANUAL_EMAIL");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    // Manual emails are sent as HTML, so line breaks are stored as <br />,
    // for new templates as well as edited ones.
    const body = tplBody.replace(/\n/g, "<br />");
    const template = {
      type: tplType,
      name: tplName,
      subject: tplSubject,
      body,
    };
    const version = selectionVersion.current;
    try {
      if (selectedTplId === "") {
        // create new template
        const id = await createEmailTemplate(template);
        if (version === selectionVersion.current) {
          setSelectedTplId(id);
        }
      } else {
        // update existing template
        await setEmailTemplate(selectedTplId, template);
      }
    } catch (error) {
      // Shown even if staff have moved to another template, so they know it was not saved.
      setErrorMessage(get(error, "response.data.error", "Failed to save the email template."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedTplId === "") {
      return;
    }
    setIsDeleting(true);
    setErrorMessage("");
    const version = selectionVersion.current;
    try {
      await removeEmailTemplate(selectedTplId);
      if (version === selectionVersion.current) {
        setSelectedTplId("");
        setTplName("");
        setTplSubject("");
        setTplBody("");
        setTplType("MANUAL_EMAIL");
      }
    } catch (error) {
      // The template is kept selected so staff can see it was not deleted and try again.
      setErrorMessage(get(error, "response.data.error", "Failed to delete the email template."));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="columns">
      <div className="column is-one-third">
        <div className="mb-6">
          <TableHeader>
            <div className="tag is-info is-radiusless">Manual Email Templates</div>
            <NewButton className="button is-small" onClick={handleClickNew}>
              <span className="icon is-small">
                <i className="fas fa-plus"></i>
              </span>
              <span>New</span>
            </NewButton>
          </TableHeader>
          <div className="mb-2">
            <PlaceholderHelpLink anchor="email-manual-workflow" />
          </div>
          <table className="table is-striped is-hoverable is-size-7">
            <thead>
              <tr>
                <td />
              </tr>
            </thead>
            <tbody>
              {orderBy(manualTemplates, ["name"], ["asc"]).map((tpl) => (
                <ClickableRow
                  key={tpl.id}
                  className={tpl.id === selectedTplId ? "has-background-info-light" : ""}
                  onClick={() => handleClickTemplate(tpl)}
                >
                  <td>{tpl.name}</td>
                </ClickableRow>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div className="tag is-info is-radiusless">Custom Email Templates</div>
          <div className="my-2">
            <PlaceholderHelpLink anchor="email-custom" />
          </div>
          <table className="table is-striped is-hoverable is-size-7">
            <thead>
              <tr>
                <td />
              </tr>
            </thead>
            <tbody>
              {orderBy(customTemplates, ["name"], ["asc"]).map((tpl) => (
                <ClickableRow
                  key={tpl.id}
                  className={tpl.id === selectedTplId ? "has-background-info-light" : ""}
                  onClick={() => handleClickTemplate(tpl)}
                >
                  <td>{tpl.name}</td>
                </ClickableRow>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="column is-two-thirds">
        <div className="columns">
          <div className="column">
            <TextInputField
              label="Name"
              isRequired
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              hasError={!tplName}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <TextInputField
              label="Subject"
              isRequired
              value={tplSubject}
              onChange={(e) => setTplSubject(e.target.value)}
              hasError={!tplSubject}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Textarea
              label="Body"
              isRequired
              rows={20}
              value={tplBody}
              onChange={(e) => setTplBody(e.target.value)}
              hasError={!tplBody}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <div className="field is-grouped">
              <p className="control">
                <button
                  className={`${isSubmitting ? "is-loading " : ""}button is-small is-info`}
                  disabled={!tplName || !tplSubject || !tplBody || isSubmitting || isDeleting}
                  onClick={handleSubmit}
                >
                  {selectedTplId === "" ? "Create" : "Update"}
                </button>
              </p>
              {selectedTplId === "" || tplType !== "MANUAL_EMAIL" ? null : (
                <p className="control">
                  <button
                    className={`${isDeleting ? "is-loading " : ""}button is-small is-danger`}
                    disabled={!tplName || !tplSubject || !tplBody || isSubmitting || isDeleting}
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </p>
              )}
            </div>
            {errorMessage ? <p className="help is-danger">{errorMessage}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const TableHeader = styled.div`
  display: flex;
  align-items: center;
`;

const NewButton = styled.button`
  margin-left: auto;
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

export default EmailTemplate;
